import { longTermReturnPct } from './householdFromDraft';
import {
  DEFAULT_ANNUAL_RATE_PCT,
  clampAnnualRatePct,
  weightedPortfolioRatePct,
} from './investmentCompounding';

export const IVD_PROJECTION_YEARS = 20;
export const IVD_SERIES_STEP_YEARS = 2;

export type BalanceSeriesPoint = { year: number; balance: number };

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Portfolio-weighted long-term rate used as the Investments vs debt default. */
export function seedInvestmentsVsDebtReturnPct(
  holdings: { value: number; expectedReturnPct?: number }[],
): number {
  if (holdings.length === 0) return DEFAULT_ANNUAL_RATE_PCT;
  return weightedPortfolioRatePct(
    holdings.map((h) => ({
      value: h.value,
      expectedReturnPct: longTermReturnPct(h.expectedReturnPct),
    })),
  );
}

/** Compound principal at a flat annual rate (no contributions) for chart points. */
export function projectInvestmentBalanceSeries(params: {
  principal: number;
  annualRatePct: number;
  startYear: number;
  years?: number;
  stepYears?: number;
}): BalanceSeriesPoint[] {
  const years = params.years ?? IVD_PROJECTION_YEARS;
  const step = params.stepYears ?? IVD_SERIES_STEP_YEARS;
  const rate = clampAnnualRatePct(params.annualRatePct) / 100;
  const principal = Math.max(0, params.principal);
  const series: BalanceSeriesPoint[] = [];

  for (let offset = 0; offset <= years; offset += 1) {
    if (offset % step !== 0 && offset !== years) continue;
    let balance = principal;
    for (let y = 0; y < offset; y += 1) {
      balance *= 1 + rate;
    }
    series.push({
      year: params.startYear + offset,
      balance: roundMoney(balance),
    });
  }
  return series;
}

function balanceAtYear(series: BalanceSeriesPoint[], year: number): number {
  let last = series[0]?.balance ?? 0;
  for (const point of series) {
    if (point.year === year) return point.balance;
    if (point.year > year) break;
    last = point.balance;
  }
  return last;
}

/** First calendar year investments are >= home loan (0 if none). */
export function findInvestmentsDebtCrossoverYear(
  mortgageSeries: BalanceSeriesPoint[],
  investmentSeries: BalanceSeriesPoint[],
  startYear: number,
): number {
  if (mortgageSeries.length === 0 || investmentSeries.length === 0) return 0;
  const endYear = Math.max(
    mortgageSeries[mortgageSeries.length - 1]?.year ?? startYear,
    investmentSeries[investmentSeries.length - 1]?.year ?? startYear,
  );
  for (let year = startYear + 1; year <= endYear; year += 1) {
    const mortgage = balanceAtYear(mortgageSeries, year);
    const investments = balanceAtYear(investmentSeries, year);
    if (investments >= mortgage && mortgage >= 0) return year;
  }
  return 0;
}
